/**
 * Microsoft Clarity — session recordings and heatmaps, to see where self-serve sign-ups stop.
 *
 * Loaded once for the whole web app (the owner chose "record every page"), so the privacy
 * controls matter more than usual: this is a multi-tenant HRMS and most screens are a real
 * company's attendance and payroll data.
 *   - Masking is set to Strict in the Clarity dashboard (Settings > Masking). Nothing here
 *     replaces that — check a recording after every change to it.
 *   - Only opaque values leave the app: a person id, the company slug, a plan state. Never a
 *     name, email, phone or any salary figure, in a tag or an event.
 *   - It runs in the browser and inside the Capacitor app alike: the phone app is this same web
 *     bundle in a WebView, so the one script covers both. Every session carries a `platform` tag
 *     (web / android / ios) to tell them apart. It does not run on the management host, which is
 *     the operator's cross-tenant panel, not a customer's.
 *   - The project id is public (it is in the page source of every site that uses Clarity).
 *     `VITE_CLARITY_PROJECT_ID=""` at build time turns it off without a code change.
 */
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { isPlatformHost } from './platformHost.js';

type Clarity = { (...args: unknown[]): void; q?: unknown[][] };

declare global {
  interface Window {
    clarity?: Clarity;
  }
}

const PROJECT_ID: string = import.meta.env.VITE_CLARITY_PROJECT_ID ?? 'z14k8x7lzi';

/** Where this build records: false on the management host or with an emptied id. */
export function isClarityEnabled(): boolean {
  if (typeof window === 'undefined' || !PROJECT_ID) return false;
  return !isPlatformHost();
}

function ensureClarity(): void {
  if (!isClarityEnabled() || window.clarity) return;

  // The stub Microsoft's own snippet installs: calls made before the script arrives are queued.
  const clarity = function (...args: unknown[]) {
    (clarity.q = clarity.q ?? []).push(args);
  } as Clarity;
  window.clarity = clarity;
  // `web`, `android` or `ios`: lets the dashboard split the app's sessions from the browser's.
  clarity('set', 'platform', Capacitor.getPlatform());

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${PROJECT_ID}`;
  document.head.appendChild(script);
}

/** Mount once at the app root. A no-op where Clarity is off or already loaded. */
export function useClarity(): void {
  useEffect(() => {
    ensureClarity();
  }, []);
}

/** A filter in the Clarity dashboard (company, plan state). Opaque values only. */
export function setClarityTag(key: string, value: string): void {
  if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
    window.clarity('set', key, value);
  }
}

/** Ties this person's recordings together by their id. The id, never a name or an email. */
export function identifyClarityUser(personId: string): void {
  if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
    window.clarity('identify', personId);
  }
}

/** A named funnel step, e.g. `add_employee_opened`. */
export function trackClarityEvent(name: string): void {
  if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
    window.clarity('event', name);
  }
}

export type ClarityPlanState = 'locked' | 'trial' | 'subscribed';

/**
 * Where the company stands, for filtering recordings. `locked` is the read-only state (trial ended
 * or billing lock); `trial` has an end date and no signed agreement; everything else is treated as
 * a customer, including older companies that never had a trial.
 */
export function clarityPlanState(me: {
  readOnly?: boolean;
  trialEndsAt?: string | null;
  agreementState?: string;
}): ClarityPlanState {
  if (me.readOnly) return 'locked';
  if (me.trialEndsAt && me.agreementState !== 'signed') return 'trial';
  return 'subscribed';
}
